import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IoMdEye, IoIosDocument, IoMdDownload } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import SortableColumnHeader from '../monitoring/SortableColumnHeader';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { SortValueType } from '../../../utils/sortUtils';
import AnalysisProgressPanel, {
    AnalysisProgressState,
    initialAnalysisProgress,
} from './AnalysisProgressPanel';
import AnalysisNoticeBanner from './AnalysisNoticeBanner';
import {
    getRecommendationBadgeClass,
    getRecommendationLabel,
    normalizeRecommendation,
} from './utils/statusLabels';
import TablePaginationFooter from './TablePaginationFooter';
import TableToolbar from './TableToolbar';
import TriggeredRulesModal from './TriggeredRulesModal';
import useClientTable from './hooks/useClientTable';

// AF-3418: tipos das colunas para o comparador escolher a estratégia certa
const PARECER_COLUMN_TYPES: Record<string, SortValueType> = {
    document: 'text',
    entityName: 'text',
    entityType: 'text',
    INOVAMIND: 'text',
    ESCAVADOR: 'text',
    QUOD: 'text',
    finalRecommendation: 'text',
    riskLevel: 'number',
    creditFlag: 'number',
};

const PLD_FILTER_OPTIONS = [
    { value: '', label: 'Todas' },
    { value: 'APPROVED', label: 'Aprovação' },
    { value: 'REJECTED', label: 'Reprovação' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'EMPTY', label: 'Sem decisão' },
];

// AF-3460: permite isolar os documentos que não foram avaliados, separando a causa
const ANALYSIS_STATE_FILTER_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'NO_DATA', label: 'Sem dados' },
    { value: 'ERROR', label: 'Erro na análise' },
];

const RISK_FILTER_OPTIONS = [
    { value: '', label: 'Todos' },
    // Mesmo padrão do filtro de PLD: permite achar o que ainda falta preencher
    { value: 'EMPTY', label: 'Sem risco selecionado' },
    { value: 'BAIXO', label: 'Baixo' },
    { value: 'MEDIO', label: 'Médio' },
    { value: 'ALTO', label: 'Alto' },
    { value: 'EXTREMO', label: 'Extremo' },
];

// Ordem de severidade, para o risco ordenar por gravidade e não por alfabeto
const RISK_SEVERITY: Record<string, number> = {
    BAIXO: 1,
    MEDIO: 2,
    ALTO: 3,
    EXTREMO: 4,
};

/**
 * Função pura, mantida no escopo do módulo.
 *
 * Estava declarada dentro do componente, depois do hook de tabela que a usa no acessor de
 * ordenação — funcionava por closure, mas dependia da ordem de avaliação do corpo do
 * componente. No módulo não há essa fragilidade.
 */
/** Soma os contadores por provider (os lotes são carregados em paralelo). */
const sumCounters = (counters: Record<string, number>): number =>
    Object.values(counters).reduce((total, value) => total + (value || 0), 0);

/**
 * Rótulo da DECISÃO de PLD (coluna "Recomendação PLD" e resumo).
 *
 * Vocabulário próprio de propósito: aqui é a decisão do analista ("Aprovação"/"Reprovação"),
 * não o estado do documento num bureau ("Aprovado"/"Reprovado"). Só assume os valores que o
 * select oferece, então NO_DATA/ERROR não aparecem por este caminho.
 *
 * Para o estado de um PROVIDER, use getRecommendationLabel (compartilhado).
 */
const translatePldDecision = (recommendation: string): string => {
    const translations: Record<string, string> = {
        APPROVED: 'Aprovação',
        REJECTED: 'Reprovação',
        PENDING: 'Pendente',
    };
    return translations[recommendation] || recommendation;
};

interface BatchOption {
    sk: string;
    identification: string;
    id: string;
    documentsCount: string;
    provider: string;
    createdAt: string;  // Data de criação do lote
}

interface DocumentPLDAnalysis {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    providers: {
        INOVAMIND: { recommendation: string; reasons: string[]; batchId: string } | null;
        ESCAVADOR: { recommendation: string; reasons: string[]; batchId: string } | null;
        QUOD: { recommendation: string; reasons: string[]; batchId: string } | null;
    };
    finalRecommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '';
    riskLevel?: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '';
    creditFlag?: boolean;
    observations: string;
}

const RecommendationEngineParecerView: React.FC = () => {
    const { batchSk } = useParams<{ batchSk: string }>();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sendingNotifications, setSendingNotifications] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Estado do parecer salvo
    const [savedReport, setSavedReport] = useState<{id: string, batchSk: string, savedAt: string, analystId?: string} | null>(null);

    // Dados dos selects
    const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
    const [selectedBatches, setSelectedBatches] = useState({
        INOVAMIND: '',
        ESCAVADOR: '',
        QUOD: ''
    });
    
    // Armazenar identifications dos lotes carregados
    const [batchIdentifications, setBatchIdentifications] = useState<{[key: string]: string}>({});

    // Dados dos documentos
    const [documents, setDocuments] = useState<DocumentPLDAnalysis[]>([]);
    // AF-3418: guarda o documento inteiro em vez de uma lista de strings achatadas.
    // O modal precisa da estrutura por provider para agrupar e mostrar a recomendação
    // de cada um.
    const [rulesModalDocument, setRulesModalDocument] = useState<DocumentPLDAnalysis | null>(null);

    // AF-3418: progresso por etapas (substitui o overlay com barra de porcentagem)
    const [progress, setProgress] = useState<AnalysisProgressState>(initialAnalysisProgress);
    const analyzedByProvider = useRef<Record<string, number>>({});
    const loadedByProvider = useRef<Record<string, number>>({});
    const totalByProvider = useRef<Record<string, number>>({});

    // AF-3418: ordenação, busca e paginação da tabela do parecer
    const [pldFilter, setPldFilter] = useState('');
    const [riskFilter, setRiskFilter] = useState('');
    const [analysisStateFilter, setAnalysisStateFilter] = useState('');

    const parecerFilterFn = useCallback(
        (doc: DocumentPLDAnalysis) => {
            if (pldFilter === 'EMPTY' && doc.finalRecommendation) return false;
            if (pldFilter && pldFilter !== 'EMPTY' && doc.finalRecommendation !== pldFilter) return false;
            // 'EMPTY' cobre tanto ausência de valor quanto string vazia
            if (riskFilter === 'EMPTY' && doc.riskLevel) return false;
            if (riskFilter && riskFilter !== 'EMPTY' && doc.riskLevel !== riskFilter) return false;
            // AF-3460: filtra pelo estado de análise em qualquer um dos providers
            if (analysisStateFilter) {
                const hasState = [
                    doc.providers.INOVAMIND,
                    doc.providers.ESCAVADOR,
                    doc.providers.QUOD,
                ].some(p => p?.recommendation === analysisStateFilter);
                if (!hasState) return false;
            }
            return true;
        },
        [pldFilter, riskFilter, analysisStateFilter]
    );

    const parecerSearchableFields = useCallback(
        (doc: DocumentPLDAnalysis) => [doc.document, doc.entityName],
        []
    );

    const parecerValueAccessor = useCallback((doc: DocumentPLDAnalysis, field: string) => {
        if (field === 'INOVAMIND' || field === 'ESCAVADOR' || field === 'QUOD') {
            // Ordena pelo rótulo traduzido; provider ausente fica no fim (valor vazio)
            const provider = doc.providers[field as keyof typeof doc.providers];
            // Provider usa o rótulo compartilhado; a decisão de PLD tem vocabulário próprio
            return provider ? getRecommendationLabel(provider.recommendation) : '';
        }
        if (field === 'riskLevel') return doc.riskLevel ? RISK_SEVERITY[doc.riskLevel] : null;
        if (field === 'creditFlag') return doc.creditFlag ? 1 : 0;
        if (field === 'finalRecommendation') {
            return doc.finalRecommendation ? translatePldDecision(doc.finalRecommendation) : '';
        }
        return (doc as unknown as Record<string, unknown>)[field];
    }, []);

    const table = useClientTable<DocumentPLDAnalysis>({
        rows: documents,
        columnTypes: PARECER_COLUMN_TYPES,
        valueAccessor: parecerValueAccessor,
        searchableFields: parecerSearchableFields,
        filterFn: parecerFilterFn,
        initialPageSize: 25,
    });

    const clearTableFilters = () => {
        table.setSearch('');
        setPldFilter('');
        setRiskFilter('');
        setAnalysisStateFilter('');
    };

    /**
     * AF-3460: separa os dois casos de "não avaliado", porque pedem ações diferentes.
     *
     * ERRO é reprocessável (bureau falhou, regras não carregaram) e continua bloqueando o
     * salvamento. SEM_DADOS não é reprocessável — o bureau respondeu e o documento não tem
     * informação naquela base — então apenas avisa.
     */
    const providersOf = (doc: DocumentPLDAnalysis) => [
        doc.providers.INOVAMIND,
        doc.providers.ESCAVADOR,
        doc.providers.QUOD,
    ];

    const documentsWithError = documents.filter(doc =>
        providersOf(doc).some(p => p?.recommendation === 'ERROR')
    );

    const documentsWithoutData = documents.filter(doc =>
        // Só conta como "sem dados" quando não há erro no mesmo documento: o erro é a
        // informação mais importante e já é sinalizado à parte.
        !providersOf(doc).some(p => p?.recommendation === 'ERROR') &&
        providersOf(doc).some(p => p?.recommendation === 'NO_DATA')
    );

    useEffect(() => {
        loadBatchOptions();
    }, []);

    const loadBatchOptions = async () => {
        try {
            // Usar BFF service em vez de chamada direta
            const data = await recommendationBFFService.fetchBatchList();
            
            // Filtrar apenas lotes dos providers PLD e status COMPLETED
            // Excluir lotes do CAE (Credit Analysis Engine) que usam prefixo "CAE" na identificação
            const pldProviders = ['INOVAMIND', 'ESCAVADOR', 'QUOD-PJ'];
            let filteredBatches = data.list.filter((batch: any) =>
                pldProviders.includes(batch.provider) &&
                batch.status === 'COMPLETED' &&
                !batch.identification?.toUpperCase().startsWith('CAE')
            );

            // Filtrar últimos 15 dias
            const fifteenDaysAgo = new Date();
            fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
            
            filteredBatches = filteredBatches.filter((batch: any) => {
                if (!batch.createdAt) return true; // Manter lotes sem data (retrocompatibilidade)
                
                // Converter formato brasileiro "DD/MM/YYYY HH:MM" para Date
                const parseBrazilianDate = (dateStr: string): Date => {
                    const [datePart, timePart] = dateStr.split(' ');
                    const [day, month, year] = datePart.split('/');
                    const [hours, minutes] = (timePart || '00:00').split(':');
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                };
                
                const batchDate = parseBrazilianDate(batch.createdAt);
                return batchDate >= fifteenDaysAgo;
            });

            // Ordenar por data (mais recente primeiro)
            filteredBatches.sort((a: any, b: any) => {
                const parseBrazilianDate = (dateStr: string): Date => {
                    if (!dateStr) return new Date(0);
                    const [datePart, timePart] = dateStr.split(' ');
                    const [day, month, year] = datePart.split('/');
                    const [hours, minutes] = (timePart || '00:00').split(':');
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
                };
                
                const dateA = parseBrazilianDate(a.createdAt || '');
                const dateB = parseBrazilianDate(b.createdAt || '');
                return dateB.getTime() - dateA.getTime();
            });

            setBatchOptions(filteredBatches);

        } catch (error) {
            console.error('Erro ao carregar lotes:', error);
            setError('Erro ao carregar lista de lotes');
        }
    };

    const handleBatchSelection = (provider: string, batchSk: string) => {
        setSelectedBatches(prev => ({
            ...prev,
            [provider]: batchSk
        }));
        
        // Não precisa carregar último parecer aqui
        // O checkExistingParecer já faz isso quando todos os lotes são selecionados
    };
    
    const clearFilters = () => {
        setSelectedBatches({
            INOVAMIND: '',
            ESCAVADOR: '',
            QUOD: ''
        });
        setDocuments([]);
        setSavedReport(null); // Limpar estado do parecer salvo
    };

    const loadDocuments = async () => {
        const selectedCount = Object.values(selectedBatches).filter(sk => sk).length;
        if (selectedCount === 0) {
            toast.warning('Selecione pelo menos um lote para carregar os documentos');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            // AF-3418: estado por etapas, alimentado pelos mesmos callbacks do BFF
            setProgress({ ...initialAnalysisProgress, stage: 'loading-reports' });

            const allDocuments = new Map<string, DocumentPLDAnalysis>();

            // Contadores por provider, para o painel somar em vez de sobrescrever
            analyzedByProvider.current = {};
            loadedByProvider.current = {};
            totalByProvider.current = {};

            // ✅ OTIMIZAÇÃO: Carregar lotes em paralelo ao invés de sequencial
            const batchPromises = Object.entries(selectedBatches)
                .filter(([_, batchSk]) => batchSk)
                .map(async ([provider, batchSk]) => {
                    const _normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;

                    try {
                        console.log(`🔄 Carregando lote ${provider} em paralelo...`);

                        const batchData = await recommendationBFFService.analyzeBatch(
                            batchSk,
                            (processed, total) => {
                                // Os lotes rodam em paralelo: guardamos o valor de cada
                                // provider e exibimos a soma, senão o contador ficaria
                                // pulando entre os providers
                                analyzedByProvider.current[provider] = processed;
                                totalByProvider.current[provider] = total;
                                setProgress(prev => ({
                                    ...prev,
                                    stage: 'applying-rules',
                                    analyzed: sumCounters(analyzedByProvider.current),
                                    reportsTotal: sumCounters(totalByProvider.current) || prev.reportsTotal,
                                    currentProvider: provider,
                                }));
                            },
                            {
                                onReportsPage: ({ loaded }) => {
                                    loadedByProvider.current[provider] = loaded;
                                    setProgress(prev => ({
                                        ...prev,
                                        stage: prev.stage === 'applying-rules' ? prev.stage : 'loading-reports',
                                        reportsLoaded: sumCounters(loadedByProvider.current),
                                        currentProvider: provider,
                                    }));
                                },
                            }
                        );

                        await enrichWithPLDRecommendations(batchData, provider);
                        return { provider, batchData, success: true };
                    } catch (error) {
                        console.error(`❌ Erro ao carregar lote ${provider}:`, error);
                        toast.error(`Erro ao carregar dados do ${provider}`);
                        return { provider, batchData: null, success: false };
                    }
                });

            // Aguardar todos os lotes carregarem em paralelo
            const batchResults = await Promise.all(batchPromises);

            // Processar resultados
            setProgress(prev => ({ ...prev, stage: 'consolidating', currentProvider: undefined }));
            const identifications: {[key: string]: string} = {};
            
            for (const { provider, batchData, success } of batchResults) {
                if (!success || !batchData) continue;

                try {
                    // 🎯 Capturar identification do lote
                    if (batchData.identification) {
                        identifications[provider] = batchData.identification;
                        console.log(`📋 ${provider} identification: ${batchData.identification}`);
                    }
                    
                    // Processar documentos do lote
                    if (batchData.reports && Array.isArray(batchData.reports)) {
                        // Agrupar reports por documento para unificar CNPJs duplicados
                        const documentMap = new Map<string, {
                            report: any;
                            allReasons: Set<string>;
                            recommendation: string;
                        }>();

                        batchData.reports.forEach((report: any) => {
                            const document = report.document;
                            
                            // Normalizar recomendação PT → EN
                            //
                            // AF-3460 (correção): esta tela mantinha a própria cadeia de
                            // if/else para normalizar, duplicando o normalizeRecommendation
                            // compartilhado que a tela de recomendações já usava. Quando
                            // SEM_DADOS foi introduzido, a cadeia daqui não recebeu o caso e
                            // o valor caía no default 'ERROR' — mesmo documento aparecia
                            // "Sem dados" em recomendações e "erro na análise" no parecer.
                            //
                            // Passa a usar a função compartilhada: com uma única fonte de
                            // normalização, as telas não podem mais divergir.
                            const normalizedRec = normalizeRecommendation(
                                report.backend_recommendation
                            );
                            
                            if (!documentMap.has(document)) {
                                // Primeira ocorrência
                                documentMap.set(document, {
                                    report: report,
                                    allReasons: new Set(report.backend_reasons || []),
                                    recommendation: normalizedRec
                                });
                            } else {
                                // Documento duplicado - consolidar
                                const existing = documentMap.get(document)!;
                                
                                // Consolidar regras
                                if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                                    report.backend_reasons.forEach((rule: string) => existing.allReasons.add(rule));
                                }
                                
                                // ✅ Consolidar credit_flag (OR lógico - se qualquer report tiver, marca como true)
                                if (report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO') {
                                    if (!existing.report.backend_credit_flag) {
                                        existing.report.backend_credit_flag = true;
                                    }
                                }
                                
                                // AF-3418: prioridade REJECTED > ERROR > PENDING > APPROVED
                                // AF-3460: prioridade REJECTED > ERROR > PENDING > NO_DATA >
                                // APPROVED, igual à tela de recomendações. NO_DATA acima de
                                // APPROVED para não esconder que um report não tinha dado.
                                if (normalizedRec === 'REJECTED') {
                                    existing.recommendation = 'REJECTED';
                                } else if (normalizedRec === 'ERROR' && existing.recommendation !== 'REJECTED') {
                                    existing.recommendation = 'ERROR';
                                } else if (
                                    normalizedRec === 'PENDING' &&
                                    existing.recommendation !== 'REJECTED' &&
                                    existing.recommendation !== 'ERROR'
                                ) {
                                    existing.recommendation = 'PENDING';
                                } else if (
                                    normalizedRec === 'NO_DATA' &&
                                    existing.recommendation === 'APPROVED'
                                ) {
                                    existing.recommendation = 'NO_DATA';
                                }
                                // Se for APPROVED, mantém o existente (não piora)
                                
                                // Atualizar nome se o novo tiver e o existente não
                                const existingHasName = existing.report.name?.trim() || existing.report.nome?.trim();
                                const currentHasName = report.name?.trim() || report.nome?.trim();
                                if (currentHasName && !existingHasName) {
                                    existing.report = report;
                                }
                            }
                        });

                        // Adicionar/atualizar documentos no mapa geral
                        documentMap.forEach((consolidated) => {
                            const report = consolidated.report;
                            const document = report.document;
                            const entityType = report.backend_document_type ||
                                (document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');

                            if (!allDocuments.has(document)) {
                                console.log('📝 Criando novo documento:', { 
                                    document, 
                                    provider,
                                    backend_credit_flag: report.backend_credit_flag,
                                    backend_recommendation: report.backend_recommendation
                                });
                                allDocuments.set(document, {
                                    document,
                                    entityName: '',
                                    entityType,
                                    providers: {
                                        INOVAMIND: null,
                                        ESCAVADOR: null,
                                        QUOD: null
                                    },
                                    finalRecommendation: '',
                                    creditFlag: report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO' || false,
                                    observations: ''
                                });
                            }

                            const docAnalysis = allDocuments.get(document)!;
                            
                            // ✅ Atualizar creditFlag se vier do backend (OR lógico - se qualquer provider sinalizar)
                            if (report.backend_credit_flag || report.backend_recommendation === 'SINALIZADO_CREDITO') {
                                docAnalysis.creditFlag = true;
                            }

                            // Atualizar tipo se vier do backend
                            if (report.backend_document_type) {
                                docAnalysis.entityType = report.backend_document_type;
                            }

                            // Atualizar dados do provider
                            const providerKey = provider === 'QUOD-PJ' ? 'QUOD' : provider as keyof typeof docAnalysis.providers;

                            // AF-3460 (correção): consolidated.recommendation JÁ vem
                            // normalizado do bloco acima. Este switch normalizava de novo e,
                            // por não conhecer NO_DATA, jogava o valor no default 'ERROR' —
                            // segundo ponto onde "sem dados" virava "erro na análise".
                            const recommendation = consolidated.recommendation || 'ERROR';
                            const reasons = Array.from(consolidated.allReasons);

                            docAnalysis.providers[providerKey] = {
                                recommendation,
                                reasons,
                                batchId: selectedBatches[provider as keyof typeof selectedBatches] || ''
                            };

                            // Atualizar nome com prioridade: INOVAMIND > ESCAVADOR > QUOD
                            const currentName = report.name?.trim() || report.nome?.trim() || '';
                            if (currentName) {
                                if (providerKey === 'INOVAMIND') {
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'ESCAVADOR' && !docAnalysis.entityName) {
                                    docAnalysis.entityName = currentName;
                                } else if (providerKey === 'QUOD' && !docAnalysis.entityName) {
                                    docAnalysis.entityName = currentName;
                                }
                            }
                        });
                    }

                } catch (error) {
                    console.warn(`Erro ao carregar lote ${batchSk} do ${provider}:`, error);
                }
            }

            setDocuments(Array.from(allDocuments.values()).map(doc => {
                // Calcular finalRecommendation baseado nas recomendações dos providers
                // Prioridade: REJECTED > PENDING > APPROVED (pior caso)
                let finalRecommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '' = '';
                let riskLevel: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '' = '';
                
                const recommendations = [
                    doc.providers.INOVAMIND?.recommendation,
                    doc.providers.ESCAVADOR?.recommendation,
                    doc.providers.QUOD?.recommendation
                ].filter(Boolean);
                
                // AF-3418: com qualquer provider em ERRO não há como sugerir recomendação
                // final. Antes, o else final caía em APPROVED, então um provider que falhou
                // podia contribuir para um parecer "aprovado".
                // AF-3460: nem ERRO nem SEM_DADOS sugerem recomendação automática. Nos dois
                // casos parte dos providers não foi avaliada, então a decisão é do analista.
                // A diferença entre eles está em bloquear ou não o salvamento, não aqui.
                const hasError = recommendations.some(r => r === 'ERROR' || r === 'NO_DATA');
                
                if (recommendations.length > 0 && !hasError) {
                    if (recommendations.some(r => r === 'REJECTED')) {
                        finalRecommendation = 'REJECTED';
                    } else if (recommendations.some(r => r === 'PENDING')) {
                        finalRecommendation = 'PENDING';
                    } else {
                        finalRecommendation = 'APPROVED';
                        // ✅ Se TODOS aprovados → Risco BAIXO
                        if (recommendations.every(r => r === 'APPROVED')) {
                            riskLevel = 'BAIXO';
                        }
                    }
                }
                
                return {
                    ...doc,
                    finalRecommendation,
                    riskLevel: riskLevel || doc.riskLevel // Manter riskLevel existente se não calculado
                };
            }));

            // ✅ REGRA DE APROVAÇÃO AUTOMÁTICA
            // Se TODOS os providers = APROVADO → PLD = APROVADO + Risco = BAIXO
            const allDocumentsArray = Array.from(allDocuments.values());
            const allApproved = allDocumentsArray.every(doc => {
                const recommendations = [
                    doc.providers.INOVAMIND?.recommendation,
                    doc.providers.ESCAVADOR?.recommendation,
                    doc.providers.QUOD?.recommendation
                ].filter(Boolean);
                
                return recommendations.length > 0 && recommendations.every(r => r === 'APPROVED');
            });
            
            if (allApproved && allDocumentsArray.length > 0) {
                console.log('✅ Todos os providers aprovados - Aplicando defaults automáticos');
                // Aplicar defaults via refs (assumindo que existem refs para os campos)
                // Nota: Isso será aplicado quando os campos forem renderizados
                // Por enquanto, vamos apenas logar. A aplicação real será no useEffect dos documentos
            }

            // Verificar se já existe parecer salvo para esta combinação de lotes
            await checkExistingParecer();
            
            // 🎯 Salvar identifications no estado
            setBatchIdentifications(identifications);
            console.log('📋 Identifications salvos:', identifications);

            setProgress(prev => ({ ...prev, stage: 'done', currentProvider: undefined }));

        } catch (error) {
            console.error('Erro ao carregar documentos:', error);
            const message =
                error instanceof Error ? error.message : 'Erro ao carregar documentos dos lotes';
            setError('Erro ao carregar documentos dos lotes');
            setProgress(prev => ({ ...prev, stage: 'error', errorMessage: message }));
        } finally {
            setLoading(false);
        }
    };

    const checkExistingParecer = async () => {
        try {
            console.log('🔍 Verificando parecer existente...');
            console.log('📦 selectedBatches:', selectedBatches);
            
            // Criar identificador único baseado nos lotes selecionados
            const batchKey = Object.entries(selectedBatches)
                .filter(([_, sk]) => sk)
                .sort()
                .map(([provider, sk]) => `${provider}:${sk}`)
                .join('|');

            console.log('🔑 batchKey:', batchKey);

            if (!batchKey) {
                console.log('⚠️ batchKey vazio, pulando verificação');
                return;
            }

            // Verificar se existe parecer salvo no backend usando BFF Service
            console.log('📡 Chamando checkPLDReportExists...');
            const result = await recommendationBFFService.checkPLDReportExists(selectedBatches);
            console.log('📥 Resultado do check:', result);
            
            if (result.exists) {
                console.log('✅ Parecer encontrado!', result);
                setSavedReport({
                    id: result.report_id,
                    batchSk: result.batch_sk || '',
                    savedAt: new Date(result.saved_at).toLocaleDateString('pt-BR'),
                    analystId: result.analyst_id
                });
                
                // Carregar dados do parecer salvo nos documentos
                if (result.documents && result.documents.length > 0) {
                    console.log('📋 Carregando dados do parecer salvo:', result.documents.length, 'documentos');
                    console.log('📋 Documentos salvos:', result.documents);
                    
                    // Atualizar documentos com dados salvos
                    setDocuments(prevDocs => {
                        return prevDocs.map(doc => {
                            const savedDoc = result.documents.find((d: any) => d.document === doc.document);
                            if (savedDoc) {
                                console.log(`✅ Restaurando dados para ${doc.document}:`, savedDoc);
                                return {
                                    ...doc,
                                    finalRecommendation: savedDoc.finalRecommendation || doc.finalRecommendation,
                                    riskLevel: savedDoc.riskLevel || doc.riskLevel,
                                    observations: savedDoc.observations || doc.observations
                                };
                            }
                            return doc;
                        });
                    });
                }
            } else {
                console.log('ℹ️ Nenhum parecer salvo encontrado');
            }
        } catch (error) {
            console.error('❌ Erro ao verificar parecer existente:', error);
            // Não mostrar erro para o usuário, é apenas uma verificação
        }
    };

    const enrichWithPLDRecommendations = async (batchData: any, provider: string) => {
        // ✅ NÃO FAZ NADA - O BFF JÁ RETORNA OS DADOS ENRIQUECIDOS
        // O método analyzeBatch do BFF já:
        // 1. Agrupa reports por documento (ESCAVADOR)
        // 2. Chama recommendation-engine com dados agrupados
        // 3. Enriquece todos os reports com backend_recommendation, backend_reasons, etc.
        
        console.log(`✅ Parecer: Dados já enriquecidos pelo BFF - ${batchData.reports?.length || 0} reports`);
        
        // Apenas validar que os dados vieram corretos
        if (batchData.reports && Array.isArray(batchData.reports)) {
            const enrichedCount = batchData.reports.filter((r: any) => r.backend_recommendation).length;
            console.log(`📊 Parecer: ${enrichedCount}/${batchData.reports.length} reports com recomendação`);
        }
    };

    // Função de fallback com regras hardcoded (mantida para compatibilidade)
    const _applyPLDRulesDirectlyFallback = (report: any, provider: string): string[] => {
        const reasons: string[] = [];

        if (provider === 'INOVAMIND') {
            // Aplicar regras do INOVAMIND baseadas nos dados reais
            if (report.bacen_qgi_cpf === true) {
                reasons.push('Quadro Geral de Inabilitados do Banco Central');
            }
            if (report.work_slave_cnpj === true || report.work_slave_cpf === true) {
                reasons.push('Trabalho Escravo');
            }
            if (report.criminal_record_federal_police === true) {
                reasons.push('Antecedentes Criminais (PF)');
            }
            if (report.sanctions_list === true) {
                reasons.push('Sanções OFAC');
            }
            if (report.eu_sanction_list === true) {
                reasons.push('Sanções UE');
            }
            if (report.fr_sanction_list === true) {
                reasons.push('Sanções França');
            }
            if (report.uk_sanction_list === true) {
                reasons.push('Sanções UK');
            }
            if (report.onu_sanction_lists === true) {
                reasons.push('Sanções ONU');
            }
            if (report.us_nonproliferation_sanction_list === true) {
                reasons.push('Sanções EUA');
            }
            if (report.world_bank_ineligible_sanction_list === true) {
                reasons.push('Banco Mundial');
            }
            if (report.red_notice_interpol === true) {
                reasons.push('Red Notice Interpol');
            }
            if (report.pep_search_v2 === true) {
                reasons.push('Pessoa Exposta Politicamente (PEP)');
            }
            if (report.ceis_infos_government_base_cnpj === true || report.ceis_infos_government_base_cpf === true) {
                reasons.push('CEIS');
            }
            if (report.cnep_infos_government_base_cnpj === true) {
                reasons.push('CNEP');
            }
            if (report.ceaf_infos_government_base === true) {
                reasons.push('CEAF');
            }
            if (report.ibama_assessment_cnpj === true || report.ibama_assessment_cpf === true) {
                reasons.push('IBAMA Multas');
            }
            if (report.ibama_embargo_cnpj === true || report.ibama_embargo_cpf === true) {
                reasons.push('IBAMA Embargos');
            }
        }

        else if (provider === 'ESCAVADOR') {
            // Aplicar regras do ESCAVADOR baseadas nos processos
            if (report.processos && Array.isArray(report.processos)) {
                const assuntos = report.processos.map((p: any) => p.sigla_assunto || '');

                if (assuntos.includes('CLP')) {
                    reasons.push('Crimes contra a liberdade pessoal');
                }
                if (assuntos.includes('RCAE')) {
                    reasons.push('Redução a condição análoga à de escravo');
                }
                if (assuntos.includes('RJF')) {
                    reasons.push('Recuperação Judicial/Falência');
                }
                if (assuntos.includes('DUP')) {
                    reasons.push('Duplicata');
                }
                if (assuntos.includes('COT')) {
                    reasons.push('Crimes contra a Ordem Tributária');
                }
            }
        }

        else if (provider === 'QUOD' || provider === 'QUOD-PJ') {
            // Aplicar regras do QUOD
            if (report.CompanyStatus && report.CompanyStatus !== 'ATIVO') {
                reasons.push(`Status RF: ${report.CompanyStatus}`);
            }

            // Verificar CNAE na blocklist
            const blockedCnaes = ['6201500']; // Exemplo
            if (report.MainActivity && blockedCnaes.includes(report.MainActivity)) {
                reasons.push(`CNAE bloqueado: ${report.MainActivity}`);
            }

            // Verificar pendências de crédito
            if (report.pendenciesControlCred && parseFloat(report.pendenciesControlCred) > 5000) {
                reasons.push(`Pendências de crédito: R$ ${parseFloat(report.pendenciesControlCred).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
            }

            // Verificar sócios com restrição
            if (report.partnerRestriction === true) {
                reasons.push('Sócio com restrição');
            }
        }

        // Se não há regras acionadas, não adicionar "Nenhuma restrição PLD encontrada"
        // Isso será tratado na exibição do modal

        return reasons;
    };

    /**
     * AF-3418: as edições passaram a ser identificadas pelo DOCUMENTO, não pela posição
     * no array.
     *
     * Com ordenação e paginação, o índice de `documents.map()` deixa de corresponder ao
     * índice em `documents` — editar a observação de uma linha gravaria no documento
     * errado. É a mesma classe de bug da correlação por posição que originou esta task.
     * Também deixam de mutar o objeto existente, criando um novo.
     */
    const updateDocumentField = <K extends keyof DocumentPLDAnalysis>(
        document: string,
        field: K,
        value: DocumentPLDAnalysis[K]
    ) => {
        setDocuments(prev =>
            prev.map(doc => (doc.document === document ? { ...doc, [field]: value } : doc))
        );
    };

    const updateFinalRecommendation = (document: string, recommendation: 'APPROVED' | 'REJECTED' | 'PENDING' | '') => {
        updateDocumentField(document, 'finalRecommendation', recommendation);
    };

    const updateRiskLevel = (document: string, riskLevel: 'BAIXO' | 'MEDIO' | 'ALTO' | 'EXTREMO' | '') => {
        updateDocumentField(document, 'riskLevel', riskLevel);
    };
    
    // ✅ REGRA DE APROVAÇÃO AUTOMÁTICA
    // Quando documentos são carregados, verificar se todos providers = APROVADO
    const defaultsAppliedRef = useRef(false);
    
    useEffect(() => {
        if (documents.length === 0) return;
        if (defaultsAppliedRef.current) return; // ✅ Evita loop - só aplica uma vez
        
        const allApproved = documents.every(doc => {
            const recommendations = [
                doc.providers.INOVAMIND?.recommendation,
                doc.providers.ESCAVADOR?.recommendation,
                doc.providers.QUOD?.recommendation
            ].filter(Boolean);
            
            return recommendations.length > 0 && recommendations.every(r => r === 'APPROVED');
        });
        
        if (allApproved) {
            console.log('✅ Todos os providers aprovados - Aplicando defaults: PLD=APROVADO, Risco=BAIXO');
            
            // Aplicar defaults para todos os documentos
            const updatedDocuments = documents.map(doc => ({
                ...doc,
                finalRecommendation: 'APPROVED' as const,
                riskLevel: 'BAIXO' as const
            }));
            
            setDocuments(updatedDocuments);
            defaultsAppliedRef.current = true; // ✅ Marca como aplicado
        }
    }, [documents]); // Executar quando documentos mudarem

    const updateObservations = (document: string, observations: string) => {
        updateDocumentField(document, 'observations', observations);
    };

    const _getRiskLevelBadge = (riskLevel?: string) => {
        if (!riskLevel) return null;

        const styles = {
            'BAIXO': 'bg-green-100 text-green-800 border-green-300',
            'MEDIO': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'ALTO': 'bg-red-100 text-red-800 border-red-300',
            'EXTREMO': 'bg-gray-900 text-white border-gray-900'
        };

        const labels = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto',
            'EXTREMO': 'Extremo'
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[riskLevel as keyof typeof styles]}`}>
                {labels[riskLevel as keyof typeof labels]}
            </span>
        );
    };

    /**
     * AF-3418: o achatamento em strings "PROVIDER: motivo" saiu daqui.
     * O modal recebe o documento e agrupa por provider, o que permite mostrar a
     * recomendação de cada um e distinguir "sem restrição" de regra violada.
     */
    const showRules = (doc: DocumentPLDAnalysis) => {
        setRulesModalDocument(doc);
    };

    /**
     * Badge da recomendação de um PROVIDER (colunas INOVAMIND/ESCAVADOR/QUOD).
     *
     * AF-3460 (correção): mantinha o próprio mapa de badges, que não conhecia NO_DATA e
     * caía no fallback de erro — então documento sem dados aparecia "Erro na análise" nas
     * células dos providers, a mesma divergência já corrigida na normalização. Passa a usar
     * os helpers compartilhados, que cobrem todos os estados.
     */
    const getRecommendationBadge = (recommendation: string | null) => (
        <span className={getRecommendationBadgeClass(recommendation)}>
            {getRecommendationLabel(recommendation)}
        </span>
    );

    const hasCreditFlag = (doc: DocumentPLDAnalysis): boolean => {
        return doc.creditFlag === true;
    };

    const translateRiskLevel = (riskLevel: string): string => {
        const translations: Record<string, string> = {
            'BAIXO': 'Baixo',
            'MEDIO': 'Médio',
            'ALTO': 'Alto'
        };
        return translations[riskLevel] || riskLevel;
    };

    const exportToCSV = () => {
        if (documents.length === 0) {
            toast.warning('Nenhum documento para exportar');
            return;
        }

        const headers = [
            'Documento',
            'Nome',
            'Tipo',
            'INOVAMIND',
            'ESCAVADOR',
            'QUOD',
            'Recomendação PLD',
            'Sinalizado Crédito',
            'Nível de Risco',
            'Observações',
            'Regras Acionadas'
        ];

        const csvData = documents.map(doc => [
            doc.document,
            doc.entityName,
            doc.entityType === 'CPF' ? 'PF' : 'PJ',
            doc.providers.INOVAMIND ? getRecommendationLabel(doc.providers.INOVAMIND.recommendation) : '-',
            doc.providers.ESCAVADOR ? getRecommendationLabel(doc.providers.ESCAVADOR.recommendation) : '-',
            doc.providers.QUOD ? getRecommendationLabel(doc.providers.QUOD.recommendation) : '-',
            doc.finalRecommendation ? translatePldDecision(doc.finalRecommendation) : 'Pendente',
            hasCreditFlag(doc) ? 'Sim' : 'Não',
            doc.riskLevel ? translateRiskLevel(doc.riskLevel) : 'Não informado',
            doc.observations || '',
            getAllRulesText(doc)
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `parecer-pld-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAllRulesText = (doc: DocumentPLDAnalysis): string => {
        const allRules: string[] = [];

        Object.entries(doc.providers).forEach(([provider, data]) => {
            if (data && data.reasons.length > 0) {
                data.reasons.forEach(reason => {
                    allRules.push(`${provider}: ${reason}`);
                });
            }
        });

        return allRules.join('; ');
    };

    const saveParecer = async () => {
        // Validar se todos os documentos têm pelo menos uma recomendação de provider
        const missingProviderRecommendations = documents.filter(doc =>
            !doc.providers.INOVAMIND && !doc.providers.ESCAVADOR && !doc.providers.QUOD
        );
        if (missingProviderRecommendations.length > 0) {
            toast.error('Todos os documentos devem ter pelo menos uma recomendação de provider');
            return;
        }

        // AF-3418: o parecer é um snapshot do que está na tela — o endpoint /pld-reports/check
        // devolve depois exatamente o que foi gravado aqui.
        //
        // AF-3460: o bloqueio ficou restrito a ERRO. Erro é reprocessável: recarregar os
        // lotes muda o resultado, então salvar congelaria uma análise que amanhã seria
        // diferente. "Sem dados" não é reprocessável — o bureau respondeu e o documento não
        // tem informação naquela base -, então bloquear só impedia o analista de concluir um
        // parecer legítimo. Ali apenas avisamos (ver faixa de aviso e toast abaixo).
        if (documentsWithError.length > 0) {
            const sample = documentsWithError.slice(0, 3).map(d => d.document).join(', ');
            toast.error(
                `${documentsWithError.length} documento(s) com erro na análise (${sample}` +
                `${documentsWithError.length > 3 ? '...' : ''}). Recarregue os lotes antes de salvar o parecer.`
            );
            return;
        }

        // AF-3460: sem dados não impede salvar, mas o aviso aparece no momento da ação
        // para a decisão ser consciente.
        if (documentsWithoutData.length > 0) {
            const sample = documentsWithoutData.slice(0, 3).map(d => d.document).join(', ');
            toast.warning(
                `Parecer salvo com ${documentsWithoutData.length} documento(s) sem dados para ` +
                `análise (${sample}${documentsWithoutData.length > 3 ? '...' : ''}).`
            );
        }

        try {
            setSaving(true);

            const parecerData = {
                batchSelections: selectedBatches,
                batchIdentification: Object.values(batchIdentifications).filter(Boolean).join(' | '),
                documents: documents,
                createdAt: new Date().toISOString()
            };
            
            console.log('📋 Enviando parecer com identification:', parecerData.batchIdentification);

            // Usar BFF Service para salvar parecer
            const result = await recommendationBFFService.savePLDReport(parecerData);
            console.log('Parecer salvo:', result);

            // Salvar informações do parecer salvo
            const savedAt = new Date().toLocaleDateString('pt-BR');
            setSavedReport({
                id: result.report_id,
                batchSk: result.batch_sk,
                savedAt: savedAt,
                analystId: user?.username || user?.email || 'system'
            });

            // Não mostrar toast, apenas atualizar estado para exibir mensagem inline
        } catch (error) {
            console.error('Erro ao salvar parecer:', error);
            toast.error('Erro ao salvar parecer PLD');
        } finally {
            setSaving(false);
        }
    };

    const hasNotifiableConditions = (): boolean => {
        return documents.some(doc =>
            doc.creditFlag === true || (doc.riskLevel && ['ALTO', 'EXTREMO'].includes(doc.riskLevel))
        );
    };

    const sendNotifications = async () => {
        if (!savedReport) return;

        try {
            setSendingNotifications(true);
            const parecerData = {
                batchSelections: selectedBatches,
                batchIdentification: Object.values(batchIdentifications).filter(Boolean).join(' | '),
                documents: documents,
            };
            const result = await recommendationBFFService.sendPLDNotifications(savedReport.batchSk, parecerData);
            if (result.events_published > 0) {
                toast.success(`${result.events_published} notificação(ões) enviada(s) com sucesso`);
            } else {
                toast.info('Nenhuma notificação a enviar (condições não atendidas)');
            }
        } catch (error) {
            console.error('Erro ao enviar notificações:', error);
            toast.error('Erro ao enviar notificações');
        } finally {
            setSendingNotifications(false);
        }
    };

    const getProviderOptions = (provider: string) => {
        const normalizedProvider = provider === 'QUOD' ? 'QUOD-PJ' : provider;
        return batchOptions.filter(batch => batch.provider === normalizedProvider);
    };

    return (
        <PageForm
            pageTitle="Parecer PLD - Análise Multi-Provider"
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Pareceres PLD', url: '/recommendation-engine/pareceres' },
                { label: 'Novo Parecer', url: '#' }
            ]}
            controlSlot={<div></div>}
        >
            {/*
              AF-3418: mesmo painel de etapas da tela de recomendações, em vez do overlay
              com barra de progresso. currentProvider mostra qual lote está sendo
              processado, que aqui é relevante porque são até 3 em paralelo.
            */}
            {progress.stage !== 'idle' && (
                <div className="mb-6">
                    <AnalysisProgressPanel progress={progress} />
                </div>
            )}
            
            {/* Seleção de Lotes */}
            <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Seleção de Lotes por Provider
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {['INOVAMIND', 'ESCAVADOR', 'QUOD'].map(provider => (
                        <SelectFilter
                            key={provider}
                            id={`select-${provider}`}
                            label={provider}
                            value={selectedBatches[provider as keyof typeof selectedBatches]}
                            onChange={(value) => handleBatchSelection(provider, value)}
                            defaultVoidOption={true}
                            defaultLabel="Selecione um lote..."
                            options={getProviderOptions(provider).map(batch => ({
                                value: batch.sk,
                                label: `${batch.identification} - ${batch.id} (${batch.documentsCount} docs)`
                            }))}
                            className="w-full"
                        />
                    ))}
                </div>

                <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            onClick={loadDocuments}
                            disabled={loading}
                        >
                            {loading ? 'Carregando...' : 'Carregar Documentos'}
                        </button>

                        <button
                            className="text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
                            onClick={exportToCSV}
                            disabled={documents.length === 0 || !savedReport}
                            title={!savedReport ? "Salve o parecer antes de exportar" : ""}
                        >
                            <IoMdDownload />
                            Exportar CSV
                        </button>

                        <button
                            className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={saveParecer}
                            disabled={saving || documents.length === 0}
                        >
                            <IoIosDocument />
                            {saving ? 'Salvando...' : (savedReport ? 'Salvar Nova Versão' : 'Salvar Parecer')}
                        </button>

                        <button
                            className="text-white bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={sendNotifications}
                            disabled={sendingNotifications || !savedReport || !hasNotifiableConditions()}
                            title={!savedReport ? 'Salve o parecer primeiro' : !hasNotifiableConditions() ? 'Nenhum documento com risco alto/extremo ou crédito sinalizado' : 'Enviar notificações para o Slack'}
                        >
                            {sendingNotifications ? 'Enviando...' : 'Enviar Notificações'}
                        </button>

                        {/* Feedback de parecer salvo - inline e verde */}
                        {savedReport && !saving && (
                            <div className="px-4 py-2 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center gap-2">
                                <span className="font-medium">✓ Parecer carregado</span>
                                <span className="text-sm">Salvo em: {savedReport.savedAt}</span>
                            </div>
                        )}
                    </div>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        onClick={clearFilters}
                    >
                        Limpar Filtros
                    </button>
                </div>
            </div>

            {/* Alerta de Último Parecer Salvo */}

            {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {/* Alerta: Parecer Já Existe */}
            {savedReport && documents.length > 0 && (
                <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                                ⚠️ Parecer Já Existe
                            </h4>
                            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                                Este lote já possui parecer salvo em: <strong>{savedReport.savedAt}</strong>
                                {savedReport.analystId && (
                                    <> por <strong>{savedReport.analystId}</strong></>
                                )}
                            </p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                💡 Você pode editar os campos abaixo. Ao salvar, será criada uma <strong>NOVA versão</strong> do parecer.
                                <br />
                                O parecer anterior será mantido no histórico.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Resumo */}
            {documents.length > 0 && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Total Documentos</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {documents.length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Aprovados</div>
                        <div className="text-lg font-semibold text-green-600">
                            {documents.filter(d => d.finalRecommendation === 'APPROVED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Reprovados</div>
                        <div className="text-lg font-semibold text-red-600">
                            {documents.filter(d => d.finalRecommendation === 'REJECTED').length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Pendentes</div>
                        <div className="text-lg font-semibold text-yellow-600">
                            {documents.filter(d => !d.finalRecommendation || d.finalRecommendation === 'PENDING').length}
                        </div>
                    </div>
                </div>
            )}

            {/* Tabela de Documentos */}
            {documents.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                    {/* AF-3460: avisa sobre não avaliados, separando erro de ausência de dado */}
                    <AnalysisNoticeBanner
                        errorDocuments={documentsWithError.map(d => d.document)}
                        noDataDocuments={documentsWithoutData.map(d => d.document)}
                    />

                    <TableToolbar
                        search={table.search}
                        onSearchChange={table.setSearch}
                        searchPlaceholder="Buscar por documento ou nome"
                        selects={[
                            {
                                id: 'pld',
                                label: 'Recomendação PLD',
                                value: pldFilter,
                                options: PLD_FILTER_OPTIONS,
                                // Rótulo longo: precisa caber numa linha sobre a borda
                                className: 'w-56',
                                onChange: (value) => {
                                    setPldFilter(value);
                                    table.setPage(1);
                                },
                            },
                            {
                                id: 'risk',
                                label: 'Risco',
                                value: riskFilter,
                                options: RISK_FILTER_OPTIONS,
                                className: 'w-52',
                                onChange: (value) => {
                                    setRiskFilter(value);
                                    table.setPage(1);
                                },
                            },
                            {
                                id: 'analysisState',
                                label: 'Estado da análise',
                                value: analysisStateFilter,
                                options: ANALYSIS_STATE_FILTER_OPTIONS,
                                className: 'w-52',
                                onChange: (value) => {
                                    setAnalysisStateFilter(value);
                                    table.setPage(1);
                                },
                            },
                        ]}
                        filteredCount={table.totalRows}
                        totalCount={table.unfilteredTotal}
                        onClear={clearTableFilters}
                        hasActiveFilters={Boolean(
                            table.search || pldFilter || riskFilter || analysisStateFilter
                        )}
                    />

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-slate-700">
                                <tr>
                                    {/* AF-3418: ordenação por coluna, mesmo componente da tela de monitoramento */}
                                    <SortableColumnHeader
                                        label="Documento"
                                        field="document"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Nome"
                                        field="entityName"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Tipo"
                                        field="entityType"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="INOVAMIND"
                                        field="INOVAMIND"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="ESCAVADOR"
                                        field="ESCAVADOR"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="QUOD"
                                        field="QUOD"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Recomendação PLD"
                                        field="finalRecommendation"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Nível de Risco"
                                        field="riskLevel"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Crédito Sinalizado"
                                        field="creditFlag"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Observações
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Regras
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {table.pageRows.map((doc, index) => {
                                    return (
                                    <tr key={`${doc.document}-${index}`} className="hover:bg-gray-100 dark:hover:bg-slate-600">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {doc.document}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                                            <div className="truncate" title={doc.entityName}>
                                                {doc.entityName || '-'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                {doc.entityType === 'CPF' ? 'PF' : 'PJ'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.INOVAMIND ? getRecommendationBadge(doc.providers.INOVAMIND.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.ESCAVADOR ? getRecommendationBadge(doc.providers.ESCAVADOR.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {doc.providers.QUOD ? getRecommendationBadge(doc.providers.QUOD.recommendation) :
                                                <span className="text-gray-500">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`final-recommendation-${doc.document}`}
                                                label=""
                                                value={doc.finalRecommendation}
                                                onChange={(value) => updateFinalRecommendation(doc.document, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'APPROVED', label: 'Aprovação' },
                                                    { value: 'REJECTED', label: 'Reprovação' },
                                                    { value: 'PENDING', label: 'Pendente' }
                                                ]}
                                                className={`w-full ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                } ${
                                                    doc.finalRecommendation === 'APPROVED' ? 'border-2 border-green-500' :
                                                    doc.finalRecommendation === 'REJECTED' ? 'border-2 border-red-500' :
                                                    doc.finalRecommendation === 'PENDING' ? 'border-2 border-yellow-500' :
                                                    ''
                                                }`}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <SelectFilter
                                                id={`risk-level-${doc.document}`}
                                                label=""
                                                value={doc.riskLevel || ''}
                                                onChange={(value) => updateRiskLevel(doc.document, value as any)}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione..."
                                                options={[
                                                    { value: 'BAIXO', label: 'Baixo' },
                                                    { value: 'MEDIO', label: 'Médio' },
                                                    { value: 'ALTO', label: 'Alto' },
                                                    { value: 'EXTREMO', label: 'Extremo' }
                                                ]}
                                                className={`w-full ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                } ${
                                                    doc.riskLevel === 'BAIXO' ? 'border-2 border-green-500' :
                                                    doc.riskLevel === 'MEDIO' ? 'border-2 border-yellow-500' :
                                                    doc.riskLevel === 'ALTO' ? 'border-2 border-red-500' :
                                                    doc.riskLevel === 'EXTREMO' ? 'border-2 border-gray-900' :
                                                    ''
                                                }`}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded accent-blue-600 pointer-events-none"
                                                    checked={doc.creditFlag ?? false}
                                                    readOnly
                                                    title="Valor vem das regras aplicadas (não editável)"
                                                />
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            <textarea
                                                className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-gray-600 dark:text-white ${
                                                    savedReport ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                                                }`}
                                                placeholder="Observações..."
                                                value={doc.observations}
                                                onChange={(e) => updateObservations(doc.document, e.target.value)}
                                                rows={3}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm flex items-center justify-center p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                                                onClick={() => showRules(doc)}
                                                title="Ver Regras"
                                            >
                                                <IoMdEye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}

                                {table.pageRows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={11}
                                            className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            Nenhum documento encontrado com os filtros aplicados
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <TablePaginationFooter
                        page={table.page}
                        totalPages={table.totalPages}
                        onPageChange={table.setPage}
                        pageSize={table.pageSize}
                        onPageSizeChange={table.setPageSize}
                        rangeStart={table.rangeStart}
                        rangeEnd={table.rangeEnd}
                        totalRows={table.totalRows}
                    />
                </div>
            )}

            {/* Modal de Regras */}
            <TriggeredRulesModal
                document={rulesModalDocument}
                onClose={() => setRulesModalDocument(null)}
            />
        </PageForm>
    );
};

export default RecommendationEngineParecerView;
